import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore,
  Refresh,
  Analytics,
  AccountBalance,
  Campaign,
  ShowChart,
  MonetizationOn,
  PictureAsPdf,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Restricted access - only stephan@t1fxteam.co.za
const AUTHORIZED_EMAIL = 'stephan@t1fxteam.co.za';

const TraderStatisticsReports = () => {
  const [reportData, setReportData] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const { currentUser } = useAuth();

  // Color palette for charts
  const chartColors = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff7f',
    '#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b',
    '#eb4d4b', '#6c5ce7', '#a29bfe', '#fd79a8', '#fdcb6e'
  ];

  useEffect(() => {
    // Check if user is authorized
    if (!currentUser) {
      setError('Authentication required');
      return;
    }

    const isAuthorized = currentUser.email === AUTHORIZED_EMAIL;
    setAuthorized(isAuthorized);

    if (!isAuthorized) {
      setError('Access restricted to authorized users only');
      return;
    }

    // Load available months and latest report data
    loadAvailableMonths();
    loadLatestReportData();
  }, [currentUser]);

  const loadAvailableMonths = async () => {
    try {
      const getAvailableMonths = httpsCallable(functions, 'getAvailableReportMonths');
      const result = await getAvailableMonths();
      
      if (result.data.success) {
        setAvailableMonths(result.data.months);
        if (result.data.months.length > 0) {
          setSelectedMonth(result.data.months[0].monthKey);
        }
      }
    } catch (error) {
      console.error('Error loading available months:', error);
    }
  };

  const loadLatestReportData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const getLatestReport = httpsCallable(functions, 'getLatestReportData');
      const result = await getLatestReport();
      
      if (result.data.success) {
        setReportData(result.data.data);
        setSelectedMonth(result.data.monthKey);
      } else {
        setError(result.data.message || 'No report data available');
      }
    } catch (error) {
      console.error('Error loading report data:', error);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyReport = async (monthKey) => {
    if (!monthKey) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const getMonthlyReport = httpsCallable(functions, 'getMonthlyReportData');
      const result = await getMonthlyReport({ monthKey });
      
      if (result.data.success) {
        setReportData(result.data.data);
      } else {
        setError(result.data.message || 'Month data not available');
      }
    } catch (error) {
      console.error('Error loading monthly report:', error);
      setError('Failed to load monthly report');
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (monthKey) => {
    setSelectedMonth(monthKey);
    loadMonthlyReport(monthKey);
  };

  const generateNewReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const processReports = httpsCallable(functions, 'processTraderStatisticsReports');
      const result = await processReports();
      
      if (result.data.success) {
        setReportData(result.data.data);
        loadAvailableMonths(); // Refresh available months
      } else {
        setError(result.data.error || 'Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setError('Failed to generate new report');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const exportToPDF = async () => {
    if (!reportData) return;

    setExportingPDF(true);
    setError(null);

    try {
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Add title page
      pdf.setFontSize(24);
      pdf.setTextColor(102, 126, 234); // Primary color
      pdf.text('FX Wars Trader Statistics Report', pageWidth / 2, 40, { align: 'center' });
      
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Report Period: ${selectedMonth || 'Latest'}`, pageWidth / 2, 60, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 80, { align: 'center' });
      pdf.text(`Total Trades: ${formatNumber(reportData.metadata.totalTrades)}`, pageWidth / 2, 95, { align: 'center' });
      pdf.text(`Total Commission: ${formatCurrency(reportData.accountTypeSummary.reduce((sum, item) => sum + item.totalCommission, 0))}`, pageWidth / 2, 110, { align: 'center' });
      
      // Add authorized user info
      pdf.setFontSize(10);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Authorized User: ${currentUser?.email}`, pageWidth / 2, 260, { align: 'center' });
      
      // Add new page for content
      pdf.addPage();

      // Get the report content element
      const reportElement = document.getElementById('report-content');
      if (!reportElement) {
        throw new Error('Report content not found');
      }

      // Expand all accordions for PDF
      const accordions = reportElement.querySelectorAll('.MuiAccordion-root');
      const originalStates = [];
      accordions.forEach((accordion, index) => {
        const isExpanded = accordion.getAttribute('aria-expanded') === 'true';
        originalStates[index] = isExpanded;
        if (!isExpanded) {
          const button = accordion.querySelector('.MuiAccordionSummary-root');
          if (button) button.click();
        }
      });

      // Wait for accordions to expand
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create canvas from the report content
      const canvas = await html2canvas(reportElement, {
        scale: 1.5, // Good balance between quality and file size
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        height: reportElement.scrollHeight,
        width: reportElement.scrollWidth,
        ignoreElements: (element) => {
          // Ignore scroll bars and certain interactive elements
          return element.classList?.contains('MuiAccordionSummary-expandIconWrapper');
        }
      });

      // Restore accordion states
      accordions.forEach((accordion, index) => {
        const currentlyExpanded = accordion.getAttribute('aria-expanded') === 'true';
        if (currentlyExpanded !== originalStates[index]) {
          const button = accordion.querySelector('.MuiAccordionSummary-root');
          if (button) button.click();
        }
      });

      const imgData = canvas.toDataURL('image/png');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pageWidth - 20; // Add margins
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      // If content is longer than one page, split it
      const maxHeight = pageHeight - 20; // Account for margins
      let yPosition = 10; // Top margin
      
      if (pdfHeight > maxHeight) {
        let sourceY = 0;
        const sourceHeight = imgProps.height;
        const sourceWidth = imgProps.width;
        
        while (sourceY < sourceHeight) {
          const remainingHeight = sourceHeight - sourceY;
          const sliceHeight = Math.min(remainingHeight, (maxHeight * sourceWidth) / pdfWidth);
          
          pdf.addImage(
            imgData, 
            'PNG', 
            10, // Left margin
            yPosition, 
            pdfWidth, 
            (sliceHeight * pdfWidth) / sourceWidth,
            0, // alias
            'FAST', // compression
            0, // rotation
            sourceY / sourceHeight, // sx
            0, // sy
            sourceWidth / sourceWidth, // swidth
            sliceHeight / sourceHeight // sheight
          );
          
          sourceY += sliceHeight;
          
          if (sourceY < sourceHeight) {
            pdf.addPage();
            yPosition = 10;
          }
        }
      } else {
        pdf.addImage(imgData, 'PNG', 10, yPosition, pdfWidth, pdfHeight);
      }

      // Generate filename with current date and month
      const currentDate = new Date();
      const filename = `FXWars_Trader_Statistics_Report_${selectedMonth || currentDate.toISOString().slice(0, 7)}_${currentDate.toISOString().slice(0, 10)}.pdf`;
      
      pdf.save(filename);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setError('Failed to export report to PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  if (!authorized) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Access Restricted
          </Typography>
          <Typography>
            This feature is only available to authorized users. 
            Current user: {currentUser?.email || 'Not authenticated'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (loading && !reportData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              📊 Trader Statistics Reports
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Comprehensive analytics and insights from trader data
            </Typography>
          </Box>
          <Chip 
            icon={<Analytics />}
            label={`Authorized: ${currentUser?.email}`}
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: 'white' }}>Select Month</InputLabel>
            <Select
              value={selectedMonth}
              onChange={(e) => handleMonthChange(e.target.value)}
              sx={{ 
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                '& .MuiSvgIcon-root': { color: 'white' }
              }}
            >
              {availableMonths.map((month) => (
                <MenuItem key={month.monthKey} value={month.monthKey}>
                  {month.monthKey} ({formatNumber(month.totalTrades)} trades)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={loadLatestReportData}
            disabled={loading}
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}
          >
            Refresh
          </Button>
          
          <Button
            variant="contained"
            startIcon={<Analytics />}
            onClick={generateNewReport}
            disabled={loading}
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}
          >
            Generate New Report
          </Button>
          
          <Button
            variant="contained"
            startIcon={exportingPDF ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
            onClick={exportToPDF}
            disabled={loading || exportingPDF}
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}
          >
            {exportingPDF ? 'Exporting...' : 'Export to PDF'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {reportData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div id="report-content">
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {formatNumber(reportData.metadata.totalTrades)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Trades
                        </Typography>
                      </Box>
                      <ShowChart sx={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {formatCurrency(reportData.accountTypeSummary.reduce((sum, item) => sum + item.totalCommission, 0))}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Commission
                        </Typography>
                      </Box>
                      <MonetizationOn sx={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {reportData.accountTypeSummary.length}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Account Types
                        </Typography>
                      </Box>
                      <AccountBalance sx={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {reportData.campaignSummary.length}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Active Campaigns
                        </Typography>
                      </Box>
                      <Campaign sx={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts Grid */}
            <Grid container spacing={3}>
              {/* Account Types Chart */}
              <Grid item xs={12} lg={6}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">
                      📈 Account Types Performance
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ height: 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={reportData.accountTypeSummary}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="accountType" />
                          <YAxis />
                          <RechartsTooltip formatter={(value, name) => [name === 'totalCommission' ? formatCurrency(value) : formatNumber(value), name]} />
                          <Legend />
                          <Bar dataKey="totalCommission" fill={chartColors[0]} name="Total Commission" />
                          <Bar dataKey="totalLots" fill={chartColors[1]} name="Total Lots" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Account Types Pie Chart */}
              <Grid item xs={12} lg={6}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">
                      🥧 Commission Distribution by Account Type
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ height: 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={reportData.accountTypeSummary}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ accountType, percent }) => `${accountType} ${(percent * 100).toFixed(1)}%`}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="totalCommission"
                          >
                            {reportData.accountTypeSummary.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip formatter={(value) => [formatCurrency(value), 'Total Commission']} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Instrument Groups Chart */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">
                      🎯 Instrument Groups Performance
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ height: 500 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={reportData.instrumentGroupSummary}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="baseInstrument" />
                          <YAxis />
                          <RechartsTooltip 
                            formatter={(value, name) => [name === 'totalCommission' ? formatCurrency(value) : formatNumber(value), name]}
                            labelFormatter={(label) => `Instrument: ${label}`}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="totalCommission" stackId="1" stroke={chartColors[2]} fill={chartColors[2]} name="Total Commission" />
                          <Area type="monotone" dataKey="tradeCount" stackId="2" stroke={chartColors[3]} fill={chartColors[3]} name="Trade Count" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Specific Instruments Chart */}
              <Grid item xs={12} lg={6}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">
                      💎 Gold, BTC & Silver Performance
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ height: 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={reportData.specificInstrumentSummary}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="instrument" />
                          <YAxis />
                          <RechartsTooltip formatter={(value, name) => [name === 'totalCommission' ? formatCurrency(value) : formatNumber(value), name]} />
                          <Legend />
                          <Bar dataKey="totalCommission" fill={chartColors[4]} name="Total Commission" />
                          <Bar dataKey="tradeCount" fill={chartColors[5]} name="Trade Count" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Campaign Performance */}
              <Grid item xs={12} lg={6}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">
                      📢 Campaign Performance
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ height: 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={reportData.campaignSummary.slice(0, 10)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="campaignName" angle={-45} textAnchor="end" height={100} />
                          <YAxis />
                          <RechartsTooltip formatter={(value, name) => [name === 'totalCommission' ? formatCurrency(value) : formatNumber(value), name]} />
                          <Legend />
                          <Line type="monotone" dataKey="totalCommission" stroke={chartColors[6]} strokeWidth={3} name="Total Commission" />
                          <Line type="monotone" dataKey="totalTradeCount" stroke={chartColors[7]} strokeWidth={3} name="Total Trades" />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Data Tables */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                📋 Detailed Data Tables
              </Typography>
              
              <Grid container spacing={3}>
                {/* Account Types Table */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Account Types Summary
                      </Typography>
                      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {reportData.accountTypeSummary.map((item, index) => (
                          <Box key={index} sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {item.accountType}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Commission: {formatCurrency(item.totalCommission)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Lots: {formatNumber(item.totalLots)} | Trades: {formatNumber(item.tradeCount)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Instrument Groups Table */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Instrument Groups Summary
                      </Typography>
                      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {reportData.instrumentGroupSummary.slice(0, 10).map((item, index) => (
                          <Box key={index} sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {item.baseInstrument}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Commission: {formatCurrency(item.totalCommission)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Variations: {item.variations.join(', ')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Trades: {formatNumber(item.tradeCount)} | Lots: {formatNumber(item.totalLots)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Metadata */}
            <Paper sx={{ p: 2, mt: 4, backgroundColor: '#f5f5f5' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                📊 Report Metadata
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">Total Trades</Typography>
                  <Typography variant="h6">{formatNumber(reportData.metadata.totalTrades)}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">Date Range</Typography>
                  <Typography variant="body1">
                    {new Date(reportData.metadata.dateRange.from).toLocaleDateString()} - {new Date(reportData.metadata.dateRange.to).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">Processed At</Typography>
                  <Typography variant="body1">
                    {new Date(reportData.metadata.processedAt).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">Request ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {reportData.metadata.requestId}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </div>
        </motion.div>
      )}
    </Box>
  );
};

export default TraderStatisticsReports; 